import { useState } from 'react'
import { message } from 'antd'
import { PlusCircleFilled, EditOutlined, DeleteOutlined, EnvironmentOutlined } from '@ant-design/icons'
import { router } from '@inertiajs/react'
import axios from '@/lib/axios'
import { ZeroState } from '@/components/patterns'
import { Button, IconButton, ConfirmModal, TableSearch } from '@/components/ui'
import VenueDrawer from './VenueDrawer'

interface Event {
  id: number
  name: string
  slug: string
}

interface Venue {
  id: number
  title: string
}

interface VenuesTabProps {
  event: Event
  venues: Venue[]
}

export default function VenuesTab({ event, venues }: VenuesTabProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null)
  const [deleteModal, setDeleteModal] = useState<{ visible: boolean; venueId: number | null; venueName: string }>({
    visible: false,
    venueId: null,
    venueName: ''
  })

  const handleCreateClick = () => {
    setEditingVenue(null)
    setDrawerVisible(true)
  }

  const handleEditClick = (venue: Venue) => {
    setEditingVenue(venue)
    setDrawerVisible(true)
  }

  const handleDeleteClick = (venueId: number, venueName: string) => {
    setDeleteModal({ visible: true, venueId, venueName })
  }

  const handleDelete = async () => {
    if (!deleteModal.venueId) return

    try {
      const response = await axios.delete(`/events/${event.slug}/agenda/venues/${deleteModal.venueId}`)

      if (response.data.success) {
        message.success(response.data.message)
        router.reload()
      }
    } catch (error: any) {
      message.error(error.response?.data?.errors?.join(', ') || 'Failed to delete venue')
    } finally {
      setDeleteModal({ visible: false, venueId: null, venueName: '' })
    }
  }

  const handleSearch = () => {
    setAppliedSearch(searchTerm)
  }

  const handleSearchReset = () => {
    setSearchTerm('')
    setAppliedSearch('')
  }

  const filteredVenues = venues.filter(venue =>
    venue.title.toLowerCase().includes(appliedSearch.toLowerCase())
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <TableSearch
          value={searchTerm}
          onChange={setSearchTerm}
          onSearch={handleSearch}
          onReset={handleSearchReset}
          placeholder="Search Venue Name"
          showFilter={false}
        />
        <Button variant="primary" icon={<PlusCircleFilled />} onClick={handleCreateClick}>
          Add Venue
        </Button>
      </div>

      {filteredVenues.length === 0 ? (
        <ZeroState
          icon={<EnvironmentOutlined className="text-6xl text-gray-300" />}
          title="No Venue Yet"
          description={appliedSearch ? 'No venues match your search' : 'Add venues for your event sessions'}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredVenues.map(venue => (
            <div key={venue.id} className="bg-white border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <EnvironmentOutlined className="text-blue-600 text-lg" />
                </div>
                <h3 className="font-semibold text-gray-900 flex-1">{venue.title}</h3>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  icon={<EditOutlined />}
                  onClick={() => handleEditClick(venue)}
                  block
                >
                  Edit
                </Button>
                <IconButton
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteClick(venue.id, venue.title)}
                  ariaLabel="Delete venue"
                  title="Delete"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <VenueDrawer
        event={event}
        venue={editingVenue}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false)
          setEditingVenue(null)
        }}
      />

      <ConfirmModal
        open={deleteModal.visible}
        title="Delete venue"
        onConfirm={handleDelete}
        onClose={() => setDeleteModal({ visible: false, venueId: null, venueName: '' })}
        confirmText="Yes"
        danger
      >
        Are you sure you want to delete this venue?
      </ConfirmModal>
    </div>
  )
}
